import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Pencil, X, Loader2, ListChecks } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const ENTITIES = [
  { v: "member", l: "Member profiles" },
  { v: "enhancer", l: "Enhancer profiles" },
];

function FieldModal({ field, onClose, onSaved }) {
  const editing = !!field;
  const [entity, setEntity] = useState(field?.entity || "member");
  const [label, setLabel] = useState(field?.label || "");
  const [options, setOptions] = useState((field?.options || []).join("\n"));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!label.trim()) { toast.error("Add a field label"); return; }
    const opts = options.split("\n").map((o) => o.trim()).filter(Boolean);
    if (opts.length === 0) { toast.error("Add at least one dropdown option"); return; }
    setSaving(true);
    try {
      const payload = { entity, label: label.trim(), options: opts, order: field?.order || 0, active: true };
      if (editing) await api.put(`/admin/custom-fields/${field.id}`, payload);
      else await api.post("/admin/custom-fields", payload);
      toast.success(editing ? "Field updated" : "Field added");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] grid place-items-center px-4 bg-black/40 backdrop-blur-sm" data-testid="cf-modal">
      <div className="relative w-full max-w-md bg-[#F9F7F3] rounded-3xl shadow-2xl p-8">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#4A5D6B]"><X size={20} /></button>
        <h3 className="font-serif text-2xl text-[#1A242B] mb-4">{editing ? "Edit field" : "New dropdown field"}</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-[#4A5D6B] mb-1 block">Applies to</label>
            <select disabled={editing} value={entity} onChange={(e) => setEntity(e.target.value)} data-testid="cf-entity" className={`eyw-input w-full ${editing ? "opacity-60" : ""}`}>
              {ENTITIES.map((e) => <option key={e.v} value={e.v}>{e.l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-[#4A5D6B] mb-1 block">Field label</label>
            <input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="cf-label" placeholder="e.g. Referral source" className="eyw-input w-full" />
          </div>
          <div>
            <label className="text-xs text-[#4A5D6B] mb-1 block">Dropdown options (one per line)</label>
            <textarea rows={5} value={options} onChange={(e) => setOptions(e.target.value)} data-testid="cf-options" placeholder={"Google\nFriend\nInstagram\nWalk-in"} className="eyw-input w-full resize-none text-sm" />
          </div>
          <button onClick={save} disabled={saving} data-testid="cf-save" className="eyw-btn w-full mt-2 disabled:opacity-60">
            {saving && <Loader2 className="animate-spin" size={16} />} Save field
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProfileFieldsPanel() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(null); // {} new | field edit
  const { data: fields = [], isLoading } = useQuery({
    queryKey: ["admin-custom-fields"],
    queryFn: () => api.get("/admin/custom-fields").then((r) => r.data),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-custom-fields"] });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/admin/custom-fields/${id}`),
    onSuccess: () => { toast.success("Field removed"); refresh(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const groups = ENTITIES.map((e) => ({ ...e, rows: fields.filter((f) => f.entity === e.v) }));

  return (
    <div data-testid="admin-profile-fields-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><ListChecks size={22} className="text-[#144F75]" /> Profile Fields</h2>
          <p className="text-sm text-[#4A5D6B]">Custom dropdowns staff capture on member & enhancer profiles — then filter by them.</p>
        </div>
        <button onClick={() => setModal({})} data-testid="cf-new-btn" className="eyw-btn !py-2 text-sm"><Plus size={16} /> New field</button>
      </div>

      {isLoading ? <p className="text-sm text-[#4A5D6B] flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</p> : (
        <div className="grid md:grid-cols-2 gap-5">
          {groups.map((g) => (
            <div key={g.v} className="eyw-card p-5">
              <h3 className="font-serif text-lg text-[#1A242B] mb-3">{g.l}</h3>
              {g.rows.length === 0 && <p className="text-sm text-[#4A5D6B]">No fields yet.</p>}
              <ul className="space-y-2">
                {g.rows.map((f) => (
                  <li key={f.id} data-testid={`cf-row-${f.id}`} className="rounded-xl border border-[#E5E0D8] bg-white px-3.5 py-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-[#1A242B]">{f.label}</span>
                      <div className="flex gap-1">
                        <button onClick={() => setModal(f)} data-testid={`cf-edit-${f.id}`} className="grid place-items-center h-7 w-7 rounded-lg hover:bg-[#144F75]/10 text-[#144F75]"><Pencil size={14} /></button>
                        <button onClick={() => { if (window.confirm(`Delete "${f.label}"?`)) del.mutate(f.id); }} data-testid={`cf-del-${f.id}`} className="grid place-items-center h-7 w-7 rounded-lg hover:bg-red-50 text-red-600"><Trash2 size={14} /></button>
                      </div>
                    </div>
                    <p className="text-xs text-[#8FA89B] mt-1">{(f.options || []).join(" · ")}</p>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {modal && <FieldModal field={modal.id ? modal : null} onClose={() => setModal(null)} onSaved={refresh} />}
    </div>
  );
}
